import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CreditCard, Plus, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function PaymentMethodsManager() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role_actual === "back_office" || (user?.extra_pages || []).includes("backoffice");
  const [open, setOpen] = useState(false);
  const [methods, setMethods] = useState([]);
  const [preview, setPreview] = useState("");
  const [nuovo, setNuovo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/payment-methods").then((r) => setMethods(r.data.methods || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    const name = nuovo.trim();
    if (!name) return;
    setBusy(true);
    try { const r = await api.post("/payment-methods", { name }); setMethods(r.data.methods || []); setNuovo(""); toast.success("Modalità aggiunta"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Rimuovere questa modalità di pagamento?")) return;
    try { const r = await api.delete(`/payment-methods/${id}`); setMethods(r.data.methods || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  return (
    <Card className="mb-4 p-4" data-testid="payment-methods-manager">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 text-sm font-bold" data-testid="pm-toggle">
        <CreditCard className="h-4 w-4 text-primary" /> Modalità di pagamento
        <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{methods.length}</span>
        <ChevronDown className={`ml-auto h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-muted-foreground">Elenco unico usato ovunque siano selezionabili le modalità di pagamento. Default globale: <b>Ad avviso merce pronta</b>.</p>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Anteprima menu a tendina</label>
            <Select value={preview} onValueChange={setPreview}>
              <SelectTrigger className="w-full sm:w-[420px]" data-testid="pm-preview-select"><SelectValue placeholder="Seleziona una modalità…" /></SelectTrigger>
              <SelectContent>
                {methods.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`pm-option-${m.id}`}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <Input value={nuovo} onChange={(e) => setNuovo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nuova modalità di pagamento…" className="h-9 w-full sm:w-[420px]" data-testid="pm-new-input" />
              <Button size="sm" onClick={add} disabled={busy || !nuovo.trim()} data-testid="pm-add-btn">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}Aggiungi</Button>
            </div>
          )}
          <div className="max-h-72 divide-y divide-border overflow-y-auto rounded border border-border" data-testid="pm-list">
            {methods.map((m) => (
              <div key={m.id} className="flex items-center gap-2 px-3 py-1.5 text-sm" data-testid={`pm-row-${m.id}`}>
                <span className="min-w-0 flex-1 truncate">{m.name}</span>
                {canEdit && <button type="button" onClick={() => del(m.id)} className="shrink-0 text-red-500 hover:text-red-600" title="Rimuovi" data-testid={`pm-del-${m.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
            ))}
            {methods.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Nessuna modalità configurata.</div>}
          </div>
        </div>
      )}
    </Card>
  );
}
